import React from 'react';

/**
 * Forms are declared dynamically via `options.fields`, so values are keyed by
 * field name. Concrete forms narrow this by passing `TValues`.
 */
export type FastFormValues = Record<string, unknown>;

export type FastFormErrors<TValues extends FastFormValues = FastFormValues> = Partial<
  Record<keyof TValues & string, string>
>;

export type FastFormField = {
  name: string;
  active: boolean;
  value?: unknown;
  initialValue?: unknown;
  onChange: (value: unknown, cb?: () => void) => void;
  onFocus: () => void;
  /**
   * Only flips the field's `active` flag; the provider ignores any argument,
   * but several call sites still pass the blurred value.
   */
  onBlur: (value?: unknown) => void;
};

/** A field whose stored value has a known shape. */
export type FastFormFieldOf<TValue> = Omit<FastFormField, 'value' | 'initialValue'> & {
  value?: TValue;
  initialValue?: TValue;
};

/** A field backing a text input, where the stored value is always a string. */
export type FastFormTextField = FastFormFieldOf<string>;

export type FastFormState<TValues extends FastFormValues = FastFormValues> = {
  values: TValues;
  errors: FastFormErrors<TValues>;
  fields: Record<string, FastFormField>;
  isValid: boolean;
  isSubmitting: boolean;
  formCommitTime: number;
  formInitTime: number;
};

export type FastFormSubmitCallback<TValues extends FastFormValues = FastFormValues> = (
  formState: FastFormState<TValues>,
  api?: FastFormApi<TValues>,
) => void;

export type FastFormApiOptions<TValues extends FastFormValues = FastFormValues> = {
  resetOnUnmount?: boolean;
  formName?: string;
  fields?: readonly string[];
  formInitialValues?: Partial<TValues>;
  validate?: (values: TValues) => FastFormErrors<TValues>;
};

export type FastFormApiConfig = {
  resetOnUnmount?: boolean;
  skipInitialize?: boolean;
};

/** Redux-driven submit trigger, consumed by `FastFormProvider`. */
export type FastFormSubmitAction = {
  formName: string;
  timestamp: number;
};

/** Props a consumer may pass to influence the form it is wrapped in. */
export type FastFormOwnProps<TValues extends FastFormValues = FastFormValues> = {
  formName?: string;
  formInitialValues?: Partial<TValues>;
  resetOnUnmount?: boolean;
  onSubmit?: FastFormSubmitCallback<TValues>;
};

export type FastFormApi<TValues extends FastFormValues = FastFormValues> =
  FastFormState<TValues> & {
    submit: (cb?: FastFormSubmitCallback<TValues>) => void;
    stopSubmit: () => void;
    changeValue: (name: string) => (value: unknown, cb?: () => void) => void;
    changeField: (name: string, props: Partial<FastFormField>) => () => void;
    changeValues: (
      options: FastFormApiOptions<TValues>,
      values: Partial<TValues>,
      cb?: (values: TValues) => void,
    ) => void;
    resetForm: () => void;
  };

export type FastFormContextValue = {
  resetForm: (formName: string) => void;
  getApi: (
    options: FastFormApiOptions,
    props: FastFormOwnProps,
    config: FastFormApiConfig,
  ) => FastFormApi;
};

const noop = () => {};

export const EMPTY_FAST_FORM_STATE: FastFormState = {
  values: {},
  errors: {},
  fields: {},
  isValid: true,
  isSubmitting: false,
  formCommitTime: 0,
  formInitTime: 0,
};

export default React.createContext<FastFormContextValue>({
  resetForm: noop,
  getApi: () => ({
    ...EMPTY_FAST_FORM_STATE,
    submit: noop,
    stopSubmit: noop,
    changeValue: () => noop,
    changeField: () => noop,
    changeValues: noop,
    resetForm: noop,
  }),
});
